/**
 * Type Definitions for ORCA360 CBTA Instructor Assessment Export
 *
 * These types define the structure for exporting the instructor assessment
 * to PDF and Word formats, following the ORCA360 CBTA template.
 *
 * Template Structure:
 * - Part A: Session Identification (reused from pilot/debriefing)
 * - Part B: IEC Competency Assessment (5 criteria)
 * - Part C: Instructor Recommendations
 * - Part D: Calibration & Concordance
 * - Part E: Instructor Self-Reflection
 */

import type { DebriefingPartAData } from './debriefing-export';

// =====================================================================
// IEC (INSTRUCTOR EFFECTIVENESS CRITERIA) DEFINITIONS
// =====================================================================

export const IEC_CRITERIA = ['IEC1', 'IEC2', 'IEC3', 'IEC4', 'IEC5'] as const;

export type IECCriterion = typeof IEC_CRITERIA[number];

export const IEC_FULL_NAMES: Record<IECCriterion, string> = {
  IEC1: 'Application of CBTA / EBT Principles & Role Modelling',
  IEC2: 'Management of the Learning Environment',
  IEC3: 'Instruction & Facilitation Skills',
  IEC4: 'Interaction with Trainees',
  IEC5: 'Assessment & Evaluation Judgment',
};

export const IEC_DESCRIPTIONS: Record<IECCriterion, string> = {
  IEC1: 'Demonstrates mastery of pilot competencies, applies TEM and CBTA/EBT methodology',
  IEC2: 'Creates positive, safe learning environment conducive to growth',
  IEC3: 'Delivers clear, structured instruction with facilitation over directive approach',
  IEC4: 'Engages in effective facilitative communication and encourages self-assessment',
  IEC5: 'Provides fair, evidence-based assessment aligned with observable behaviors',
};

// =====================================================================
// GRADE DEFINITIONS
// =====================================================================

export const INSTRUCTOR_GRADE_LABELS: Record<number, string> = {
  5: 'Excellent',
  4: 'Above Standard',
  3: 'Standard',
  2: 'Below Standard',
  1: 'Unsatisfactory',
};

// =====================================================================
// IOB (INSTRUCTOR OBSERVABLE BEHAVIOR) DEFINITIONS
// =====================================================================

export const IOB_DEFINITIONS: Record<string, Array<{ code: string; description: string }>> = {
  IEC1: [
    { code: 'IOB 1.1', description: 'Demonstrates mastery of pilot competencies and models correct behaviors' },
    { code: 'IOB 1.2', description: 'Applies TEM in the context of instruction/evaluation' },
    { code: 'IOB 1.3', description: 'Uses CBTA/EBT methodology for assessment decisions' },
    { code: 'IOB 1.4', description: 'References observable behaviors (OBs) in feedback' },
  ],
  IEC2: [
    { code: 'IOB 2.1', description: 'Creates positive, safe learning environment conducive to growth' },
    { code: 'IOB 2.2', description: 'Establishes psychological safety for trainee self-disclosure' },
    { code: 'IOB 2.3', description: 'Manages simulator/training resources effectively' },
    { code: 'IOB 2.4', description: 'Adapts environment to trainee learning needs' },
  ],
  IEC3: [
    { code: 'IOB 3.1', description: 'Delivers clear, structured instruction aligned with CBTA principles' },
    { code: 'IOB 3.2', description: 'Uses facilitation over directive instruction' },
    { code: 'IOB 3.3', description: 'Employs questioning techniques to promote self-discovery' },
    { code: 'IOB 3.4', description: 'Provides demonstrations when appropriate' },
  ],
  IEC4: [
    { code: 'IOB 4.1', description: 'Engages in effective facilitative communication' },
    { code: 'IOB 4.2', description: 'Listens actively and responds to trainee needs' },
    { code: 'IOB 4.3', description: 'Provides timely, specific feedback' },
    { code: 'IOB 4.4', description: 'Encourages trainee self-assessment and reflection' },
  ],
  IEC5: [
    { code: 'IOB 5.1', description: 'Provides fair, evidence-based assessment aligned with OBs' },
    { code: 'IOB 5.2', description: 'Applies grading criteria consistently (no drift)' },
    { code: 'IOB 5.3', description: 'Documents assessment rationale clearly' },
    { code: 'IOB 5.4', description: 'Identifies root causes, not just symptoms' },
  ],
};

/**
 * Look up IOB description by code
 */
export function getIOBDescription(code: string): string {
  for (const criterion of Object.values(IOB_DEFINITIONS)) {
    const iob = criterion.find(i => i.code === code);
    if (iob) return iob.description;
  }
  return '';
}

// =====================================================================
// Part A: Session Identification (reuse from debriefing-export)
// =====================================================================

export type InstructorAssessmentPartA = DebriefingPartAData;

// =====================================================================
// Part B: IEC Competency Assessment (Export Part C)
// Note: In the online view this is "Part C/D", in exports it's "Part C"
// =====================================================================

export interface InstructorObservableBehavior {
  /** IOB code, e.g., "IOB 2.1" */
  code: string;
  /** Short description of the behavior */
  description: string;
}

export interface InstructorEvidenceItem {
  /** Direct quote from transcript */
  quote: string;
  /** Timestamp of the evidence, e.g., "14:23:05" */
  timestamp: string;
  /** Validation status from anti-hallucination system */
  validation_status: 'VERIFIED' | 'PARAPHRASED' | 'SIMILAR' | 'UNVERIFIED' | 'VERBATIM';
  /** Confidence score (0-1) */
  confidence?: number;
  /** LLM validation decision (Tier 4) */
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  /** LLM reasoning for decision */
  llm_reasoning?: string;
}

export interface IECAssessment {
  /** Criterion code (IEC1-IEC5) */
  criterion: IECCriterion;
  /** Grade 1-5 */
  score: number;
  /** Instructor Observable Behaviors demonstrated */
  observableBehaviors: InstructorObservableBehavior[];
  /** Evidence items with quotes and timestamps */
  evidence: InstructorEvidenceItem[];
  /** Instructor comments linked to IOBs */
  instructorComments: string;
  /** Development Areas Analysis (Part D export) */
  analysis?: string;
  /** Professional Development Goals (Part D export) */
  goals?: string;
  /** Narrative evidence for Part C table (condensed, 1 item max) */
  narrativeEvidence?: string;
}

export interface InstructorAssessmentPartB {
  /** All 5 IEC assessments */
  iecAssessments: IECAssessment[];
}

// =====================================================================
// Part C: Instructor Recommendations (Export Part D)
// Note: Contains Development Areas Analysis + Professional Development Goals
// =====================================================================

export interface InstructorRecommendation {
  /** The IEC competency requiring focus */
  criterion: IECCriterion;
  /** Criterion full name */
  criterionName: string;
  /** Why is this an improvement opportunity? */
  improvementReason: string;
  /** Focus / Recommendations */
  recommendations: string;
}

export interface InstructorAssessmentPartC {
  /** Recommendations for instructor improvement (typically 2-3) */
  recommendations: InstructorRecommendation[];
}

// =====================================================================
// Part D: Calibration & Concordance (Export Part E)
// Note: In the online view this is "Part E", in exports it's "Part E"
// =====================================================================

export interface CalibrationItem {
  item: string;
  observation: 'Yes' | 'No' | 'Mild drift' | 'N/A';
}

export interface AICalibration {
  evidenceAligned: 'Yes' | 'No' | 'N/A';
  gradeSeverityAligned: 'Yes' | 'No' | 'N/A';
  rationaleLogicAligned: 'Yes' | 'No' | 'N/A';
  driftDetected: 'Yes' | 'No' | 'Mild drift' | 'N/A';
}

export interface PeerCalibration {
  evidenceAligned: 'Yes' | 'No' | 'N/A';
  gradeSeverityAligned: 'Yes' | 'No' | 'N/A';
  rationaleLogicAligned: 'Yes' | 'No' | 'N/A';
  driftDetected: 'Yes' | 'No' | 'Mild drift' | 'N/A';
  /** Peer calibration availability note */
  availabilityNote?: string;
}

export interface InstructorAssessmentPartD {
  aiCalibration: AICalibration;
  peerCalibration: PeerCalibration;
}

// =====================================================================
// Part F: Instructor Self-Reflection (Mandatory)
// =====================================================================

export interface InstructorAssessmentPartF {
  /** Facilitation Effectiveness */
  facilitationEffectiveness?: string;
  /** Challenges encountered */
  challenges?: string;
  /** Learning points */
  learningPoints?: string;
  /** Path Forward / Instructor Response */
  instructorResponse?: string;
  /** Legacy: Combined self-reflection text (fallback) */
  selfReflection?: string;
}

// =====================================================================
// COMPLETE INSTRUCTOR ASSESSMENT EXPORT DATA
// =====================================================================

export interface InstructorAssessmentExportData {
  partA: InstructorAssessmentPartA;  // Session Identification
  partB: InstructorAssessmentPartB;  // IEC Competency Assessment (Export Part C)
  partC: InstructorAssessmentPartC;  // Instructor Recommendations (Export Part D)
  partD: InstructorAssessmentPartD;  // Calibration & Concordance (Export Part E)
  partF: InstructorAssessmentPartF;  // Self-Reflection (Export Part F)
  /** Invocation line at end of report */
  invocationLine?: string;
  /** Metadata */
  metadata?: {
    generatedAt: string;
    version: string;
    instructorId: string;
  };
}

// =====================================================================
// DATABASE TO EXPORT MAPPING
// =====================================================================

export interface AssessmentInstructorRow {
  id: string;
  session_id: string;
  instructor_id: string;
  criterion: string;
  score: number;
  evidence: InstructorEvidenceItem[];
  analysis: string; // Development areas analysis
  goals: string; // Professional development goals
  status: 'draft' | 'published';
  version: number;
  ai_generated?: boolean;
  created_at: string;
  updated_at: string;
  // Calibration fields (Part E - added in migration 088)
  ai_evidence_aligned?: boolean | null;
  ai_grade_aligned?: boolean | null;
  ai_rationale_aligned?: boolean | null;
  ai_drift_detected?: 'Yes' | 'No' | 'Mild drift' | null;
  peer_evidence_aligned?: boolean | null;
  peer_grade_aligned?: boolean | null;
  peer_rationale_aligned?: boolean | null;
  peer_drift_detected?: 'Yes' | 'No' | 'Mild drift' | null;
}

export interface SessionForInstructorExport {
  id: string;
  session_date: string;
  aircraft_type: string;
  simulator_id?: string;
  scenario_code?: string;
  flight_phase?: string;
  session_notes?: string;
  session_start_time?: string;
  session_end_time?: string;
  time_pf_minutes?: number;
  time_pm_minutes?: number;
  location?: string;
  overall_result?: 'Pass' | 'Fail' | 'Incomplete';
  pilot_function?: 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other';
  trainee?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  instructor?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  // V2 assessment JSON (if available)
  orca360_v2_instructor_assessment?: any;
}

// =====================================================================
// EXPORT CONFIGURATION
// =====================================================================

export interface InstructorAssessmentExportOptions {
  format: 'pdf' | 'docx';
  includeLogo?: boolean;
  includeHeaders?: boolean;
  includeFooters?: boolean;
  includePageNumbers?: boolean;
  includePartA?: boolean;
  includePartB?: boolean;
  includePartC?: boolean;
  includePartD?: boolean;
  includePartE?: boolean;
  includeSignatures?: boolean;
}

// =====================================================================
// HELPER FUNCTIONS
// =====================================================================

/**
 * Helper: Create narrative evidence string for Part C table
 * Limits to first N evidence items and formats as brief narrative
 * Note: Field is 'evidence_quote' in DB, fallback to 'quote' for compatibility
 */
function createNarrativeEvidence(evidence: InstructorEvidenceItem[], limit: number = 1): string {
  if (!evidence || evidence.length === 0) return 'No evidence recorded';

  const topEvidence = evidence.slice(0, limit);
  return topEvidence
    .map((e: any) => {
      // Support multiple field name variations from different data sources:
      // - evidence_quote: Legacy DB format
      // - quote: Current DB format (from storeInstructorAssessment)
      // - evidence: Raw extraction format (before DB storage)
      const quote = e.evidence_quote || e.quote || e.evidence || 'No quote';
      const timestamp = e.timestamp || '';
      return timestamp ? `"${quote}" @${timestamp}` : `"${quote}"`;
    })
    .join('; ');
}

/**
 * Check if v2Assessment.partB is in API format (raw array) vs frontend format (object with iecAssessments)
 * API stores: partB: PartB_CompetencyAssessment[] (array directly)
 * Frontend expects: partB: { iecAssessments: IECAssessment[] } (object with iecAssessments property)
 */
function isApiPartBFormat(partB: any): partB is any[] {
  return Array.isArray(partB);
}

/**
 * Transform API partB format (array) to frontend format (object with iecAssessments)
 * API format: [{criterion, grade, observableBehaviors, evidence, comments}, ...]
 * Frontend format: { iecAssessments: [{criterion, score, observableBehaviors, evidence, instructorComments, ...}] }
 */
function transformApiPartBToFrontend(apiPartB: any[]): InstructorAssessmentPartB {
  const iecAssessments: IECAssessment[] = apiPartB.map(item => {
    // Map evidence items with correct field names
    // Supports multiple field name variations:
    // - evidence_quote: Legacy DB format
    // - quote: Current DB format (InstructorEvidence type has 'quote')
    // - evidence: Raw extraction format (before mapping to 'quote')
    const mappedEvidence = (item.evidence || []).map((e: any) => ({
      quote: e.evidence_quote || e.quote || e.evidence || '',
      timestamp: e.timestamp || '',
      validation_status: e.validation_status || 'UNVERIFIED',
      confidence: e.confidence,
      llm_decision: e.llm_decision,
      llm_reasoning: e.llm_reasoning,
    }));

    // Map observable behaviors with descriptions from IOB_DEFINITIONS
    const mappedIOBs = (item.observableBehaviors || []).map((iob: string | { code: string; description?: string }) => {
      if (typeof iob === 'string') {
        return { code: iob, description: getIOBDescription(iob) };
      }
      return {
        code: iob.code,
        description: iob.description || getIOBDescription(iob.code),
      };
    });

    return {
      criterion: item.criterion as IECCriterion,
      score: item.grade || item.score || 0,
      observableBehaviors: mappedIOBs,
      evidence: mappedEvidence,
      instructorComments: item.comments || item.instructorComments || '',
      analysis: item.analysis || '',
      goals: item.goals || '',
      narrativeEvidence: createNarrativeEvidence(mappedEvidence, 1),
    };
  });
  return { iecAssessments };
}

/**
 * Transform API partC format to frontend format
 * API field names: whyImprovement, focusRecommendations
 * Frontend field names: improvementReason, recommendations
 */
function transformApiPartCToFrontend(apiPartC: any): InstructorAssessmentPartC {
  if (!apiPartC?.recommendations || !Array.isArray(apiPartC.recommendations)) {
    return { recommendations: [] };
  }
  return {
    recommendations: apiPartC.recommendations.map((rec: any) => ({
      criterion: rec.criterion as IECCriterion,
      criterionName: rec.criterionName || IEC_FULL_NAMES[rec.criterion as IECCriterion] || rec.criterion,
      improvementReason: rec.whyImprovement || rec.improvementReason || '',
      recommendations: rec.focusRecommendations || rec.recommendations || '',
    })),
  };
}

/**
 * Map database assessment rows to Part B structure
 */
export function mapAssessmentsToPartB(assessments: AssessmentInstructorRow[]): InstructorAssessmentPartB {
  const iecAssessments: IECAssessment[] = IEC_CRITERIA.map(iec => {
    const assessment = assessments.find(a => a.criterion === iec);

    if (!assessment) {
      return {
        criterion: iec,
        score: 0,
        observableBehaviors: [],
        evidence: [],
        instructorComments: 'Not assessed',
        analysis: 'Not assessed',
        goals: 'Not assessed',
        narrativeEvidence: 'Not assessed',
      };
    }

    // Parse IOBs from analysis or evidence and look up descriptions
    const observableBehaviors: InstructorObservableBehavior[] = [];
    const iobPattern = /IOB\s*(\d+\.?\d*)/gi;
    const text = `${assessment.analysis || ''} ${assessment.goals || ''}`;
    let match;
    const seenCodes = new Set<string>(); // Deduplicate IOBs
    while ((match = iobPattern.exec(text)) !== null) {
      const iobCode = `IOB ${match[1]}`;
      if (!seenCodes.has(iobCode)) {
        seenCodes.add(iobCode);
        observableBehaviors.push({
          code: iobCode,
          description: getIOBDescription(iobCode),
        });
      }
    }

    return {
      criterion: iec,
      score: assessment.score || 0,
      observableBehaviors,
      evidence: assessment.evidence || [],
      instructorComments: assessment.analysis || '',
      analysis: assessment.analysis || '',
      goals: assessment.goals || '',
      narrativeEvidence: createNarrativeEvidence(assessment.evidence || [], 1),
    };
  });

  return { iecAssessments };
}

/**
 * Map database assessment rows to Part C structure (recommendations)
 */
export function mapAssessmentsToPartC(assessments: AssessmentInstructorRow[]): InstructorAssessmentPartC {
  // Focus on criteria with score <= 3 or those with goals
  const recommendations: InstructorRecommendation[] = assessments
    .filter(a => a.score <= 3 || a.goals)
    .sort((a, b) => (a.score || 5) - (b.score || 5)) // Lowest scores first
    .slice(0, 3) // Max 3 recommendations
    .map(a => ({
      criterion: a.criterion as IECCriterion,
      criterionName: IEC_FULL_NAMES[a.criterion as IECCriterion] || a.criterion,
      improvementReason: a.analysis || 'Analysis not available',
      recommendations: a.goals || 'No specific recommendations',
    }));

  return { recommendations };
}

/**
 * Map database calibration fields to Part D structure
 * Note: Calibration data is stored on the first assessment (applies to all IEC criteria)
 */
export function mapCalibrationData(assessments: AssessmentInstructorRow[]): InstructorAssessmentPartD {
  const firstAssessment = assessments[0]; // All criteria share same calibration data

  if (!firstAssessment) {
    return createDefaultPartD();
  }

  const boolToResponse = (value: boolean | null | undefined): 'Yes' | 'No' | 'N/A' => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'N/A';
  };

  const driftToResponse = (value: string | null | undefined): 'Yes' | 'No' | 'Mild drift' | 'N/A' => {
    if (value === 'Yes') return 'Yes';
    if (value === 'No') return 'No';
    if (value === 'Mild drift') return 'Mild drift';
    return 'N/A';
  };

  return {
    aiCalibration: {
      evidenceAligned: boolToResponse(firstAssessment.ai_evidence_aligned),
      gradeSeverityAligned: boolToResponse(firstAssessment.ai_grade_aligned),
      rationaleLogicAligned: boolToResponse(firstAssessment.ai_rationale_aligned),
      driftDetected: driftToResponse(firstAssessment.ai_drift_detected),
    },
    peerCalibration: {
      evidenceAligned: boolToResponse(firstAssessment.peer_evidence_aligned),
      gradeSeverityAligned: boolToResponse(firstAssessment.peer_grade_aligned),
      rationaleLogicAligned: boolToResponse(firstAssessment.peer_rationale_aligned),
      driftDetected: driftToResponse(firstAssessment.peer_drift_detected),
      availabilityNote: 'Peer calibration requires 5+ instructors with 5+ assessments each',
    },
  };
}

/**
 * Create default Part D (calibration) - can be populated from V2 assessment if available
 */
export function createDefaultPartD(): InstructorAssessmentPartD {
  return {
    aiCalibration: {
      evidenceAligned: 'N/A',
      gradeSeverityAligned: 'N/A',
      rationaleLogicAligned: 'N/A',
      driftDetected: 'N/A',
    },
    peerCalibration: {
      evidenceAligned: 'N/A',
      gradeSeverityAligned: 'N/A',
      rationaleLogicAligned: 'N/A',
      driftDetected: 'N/A',
      availabilityNote: 'Peer calibration requires 5+ instructors with 5+ assessments each',
    },
  };
}

/**
 * Create default Part F (self-reflection) - placeholder for instructor input
 */
export function createDefaultPartF(): InstructorAssessmentPartF {
  return {
    facilitationEffectiveness: '',
    challenges: '',
    learningPoints: '',
    instructorResponse: '',
    selfReflection: '',
  };
}

/**
 * Create complete export data from database records
 */
export function createInstructorAssessmentExportData(
  session: SessionForInstructorExport,
  assessments: AssessmentInstructorRow[],
  partA: InstructorAssessmentPartA,
  v2Assessment?: any
): InstructorAssessmentExportData {
  // Use V2 assessment data if available
  if (v2Assessment) {
    // Validate partA structure - ensure trainee/instructor objects have required name property
    // This prevents "Cannot read properties of undefined (reading 'name')" errors in export helpers
    const validatedPartA = v2Assessment.partA &&
                           typeof v2Assessment.partA === 'object' &&
                           v2Assessment.partA.trainee &&
                           typeof v2Assessment.partA.trainee === 'object' &&
                           typeof v2Assessment.partA.trainee.name === 'string' &&
                           v2Assessment.partA.instructor &&
                           typeof v2Assessment.partA.instructor === 'object' &&
                           typeof v2Assessment.partA.instructor.name === 'string'
                           ? v2Assessment.partA
                           : partA; // Fallback to passed-in partA if V2 is malformed

    // Validate partD structure (calibration from database or v2Assessment)
    const partD = v2Assessment.partD &&
                  typeof v2Assessment.partD === 'object' &&
                  v2Assessment.partD.aiCalibration &&
                  v2Assessment.partD.peerCalibration
                  ? v2Assessment.partD
                  : mapCalibrationData(assessments);

    // Validate partF structure (renamed from partE)
    const partF = v2Assessment.partF &&
                  typeof v2Assessment.partF === 'object'
                  ? v2Assessment.partF
                  : v2Assessment.partE &&
                    typeof v2Assessment.partE === 'object'
                    ? v2Assessment.partE  // Fallback to old partE for backward compatibility
                    : createDefaultPartF();

    // Transform partB: API format (array) vs frontend format (object with iecAssessments)
    // API stores: partB: [{criterion, grade, ...}, ...]
    // Frontend expects: partB: { iecAssessments: [{criterion, score, ...}] }
    const partB = v2Assessment.partB
      ? (isApiPartBFormat(v2Assessment.partB)
          ? transformApiPartBToFrontend(v2Assessment.partB)  // Transform API array to frontend object
          : (v2Assessment.partB.iecAssessments?.length > 0
              ? v2Assessment.partB  // Already in frontend format
              : mapAssessmentsToPartB(assessments)))  // Empty, fall back to database mapping
      : mapAssessmentsToPartB(assessments);  // No v2Assessment.partB, use database mapping

    // Transform partC: API field names differ from frontend
    // API uses: whyImprovement, focusRecommendations
    // Frontend uses: improvementReason, recommendations
    const partC = v2Assessment.partC
      ? transformApiPartCToFrontend(v2Assessment.partC)  // Transform to normalize field names
      : mapAssessmentsToPartC(assessments);  // No v2Assessment.partC, use database mapping

    return {
      partA: validatedPartA,
      partB,
      partC,
      partD,
      partF,
      invocationLine: 'Please verify the AI assessment of your instruction (which may contain inaccuracies due to AI limitations) and comment on your path forward. Feedback on the AI is highly appreciated.',
      metadata: {
        generatedAt: new Date().toISOString(),
        version: '1.0',
        instructorId: session.instructor?.id || '',
      },
    };
  }

  // Build from legacy assessment data
  return {
    partA,
    partB: mapAssessmentsToPartB(assessments),
    partC: mapAssessmentsToPartC(assessments),
    partD: mapCalibrationData(assessments),
    partF: createDefaultPartF(),
    invocationLine: 'Please verify the AI assessment of your instruction (which may contain inaccuracies due to AI limitations) and comment on your path forward. Feedback on the AI is highly appreciated.',
    metadata: {
      generatedAt: new Date().toISOString(),
      version: '1.0',
      instructorId: session.instructor?.id || '',
    },
  };
}
